/**
 * Utilities for handling polylines in different formats
 */

/**
 * Convert a polyline to a WKT LINESTRING format for PostgreSQL
 * 
 * @param {string} polyline - Google Maps encoded polyline or custom format
 * @returns {string} WKT LINESTRING format
 */
export function polylineToWktLineString(polyline) {
  if (!polyline) return null;
  
  const points = processPolyline(polyline);
  if (points.length < 2) return null;
  
  // Convert to WKT LINESTRING format
  // Note: PostgreSQL expects LINESTRING(lng lat, lng lat, ...)
  const pointsStr = points.map(point => `${point[1]} ${point[0]}`).join(', ');
  return `LINESTRING(${pointsStr})`;
}

/**
 * Process a polyline string in either Google encoded format or custom format
 * Returns an array of [lat, lng] arrays for database operations
 * 
 * @param {string} polyline - Polyline string in either format
 * @returns {Array} Array of [lat, lng] coordinates
 */
export function processPolyline(polyline) {
  if (!polyline) return [];
  
  // Check if it's already in the format "lat,lng|lat,lng"
  if (polyline.includes(',') && polyline.includes('|')) {
    console.log("Processing pipe-delimited polyline format");
    return polyline.split('|').map(point => {
      const [lat, lng] = point.split(',').map(parseFloat);
      return [lat, lng];
    });
  }
  
  // Otherwise assume it's encoded Google polyline and decode it
  try {
    const poly = [];
    let index = 0, lat = 0, lng = 0;
    
    while (index < polyline.length) {
      let b, shift = 0, result = 0;
      
      do {
        b = polyline.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      
      const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += dlat;
      
      shift = 0;
      result = 0;
      
      do {
        b = polyline.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      
      const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += dlng;
      
      poly.push([lat * 1e-5, lng * 1e-5]);
    }
    
    return poly;
  } catch (e) {
    console.error("Error decoding Google polyline:", e);
    return [];
  }
}

/**
 * Decode a Google Maps encoded polyline for use with Google Maps
 * This version returns the format needed for Google Maps (lat/lng as object properties)
 * 
 * @param {string} encoded - Google Maps encoded polyline
 * @returns {Array} Array of {lat, lng} objects for Google Maps
 */
export function decodePolyline(encoded) {
  if (!encoded) return [];
  
  // Check if it's already in the format "lat,lng|lat,lng"
  if (encoded.includes(',') && encoded.includes('|')) {
    console.log("Processing pipe-delimited polyline format");
    return encoded.split('|').map(point => {
      const [lat, lng] = point.split(',').map(parseFloat);
      return { lat, lng };
    });
  }
  
  // Otherwise assume it's encoded Google polyline
  try {
    const poly = [];
    let index = 0, lat = 0, lng = 0;
    
    while (index < encoded.length) {
      let b, shift = 0, result = 0;
      
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      
      const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lat += dlat;
      
      shift = 0;
      result = 0;
      
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      
      const dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
      lng += dlng;
      
      poly.push({
        lat: lat * 1e-5,
        lng: lng * 1e-5
      });
    }
    
    return poly;
  } catch (e) {
    console.error("Error decoding polyline:", e);
    return [];
  }
}